<template>
  <component
    :is="isLink ? TheNuxtLink : 'button'"
    v-bind="isLink ? linkProps : buttonProps"
    :class="`${contextClassName} ${contextSizeClassName}`"
    class="button inline-block cursor-pointer truncate rounded-3xl border border-solid text-center text-base leading-6 transition-all"
  >
    <slot />
  </component>
</template>

<!-- eslint-disable @typescript-eslint/no-unused-vars -->
<script setup lang="ts">
  import type { RouteLocationRaw } from 'vue-router';

  import type { NuxtLinkProps } from '#app';

  /**
   * * Desktop & Tablet 的按鈕高度：
   * * 38px = 24px(line-height) + 24px(padding) + 2px(border)
   */

  type TButtonType = 'filled' | 'outline' | 'unfilled' | 'tonal';
  type TButtonSize = 'large' | 'medium' | 'small';
  type TButtonHierarchy = 'primary' | 'secondary';

  interface IProps extends NuxtLinkProps {
    to?: RouteLocationRaw;
    title?: string;
    type?: TButtonType;
    size?: TButtonSize;
    hierarchy?: TButtonHierarchy;
    disabled?: boolean;
  }

  const props = withDefaults(defineProps<IProps>(), {
    to: undefined,
    title: undefined,
    type: 'filled',
    size: 'large',
    hierarchy: 'primary',
    disabled: false,
  });

  const TheNuxtLink = resolveComponent('TheNuxtLink');

  const isLink = computed((): boolean => props.to !== undefined);

  const linkProps = computed((): NuxtLinkProps => {
    const { type, hierarchy, size, disabled, ...nuxtLinkProps } = props;
    return nuxtLinkProps;
  });

  const buttonProps = computed(() => ({
    type: 'button',
    disabled: props.disabled,
    title: props.title,
  }));

  const contextClassName = computed((): string => {
    if (props.disabled) {
      return `bg-gray-300 ${props.type === 'outline' ? 'border-gray-400' : 'border-gray-300'} text-gray-600 pointer-events-none`;
    }

    if (props.hierarchy === 'secondary') {
      switch (props.type) {
        case 'outline':
          return 'bg-transparent text-blue-400 border-blue-400 hover:bg-blue-400_a1 active:bg-blue-400_a2';
        case 'unfilled':
          return 'bg-transparent text-blue-400 border-transparent hover:bg-blue-400_a1 active:bg-blue-400_a2';
        case 'tonal':
          return 'bg-blue-400_a1 text-blue-400 border-transparent hover:bg-blue-400_a2 active:bg-blue-400_a3';
        default:
          return 'bg-blue-400 text-white border-blue-400 hover:bg-blue-500 hover:border-blue-500 active:bg-blue-600 active:border-blue-600';
      }
    }

    switch (props.type) {
      case 'outline':
        return 'bg-white text-black border-black hover:shadow-button hover:shadow-blue-400 active:shadow-button active:shadow-brand-400 transition-shadow';
      case 'unfilled':
        return 'bg-white text-black border-white hover:bg-gray-600_a25 hover:border-transparent active:bg-gray-600_a35 active:border-transparent';
      case 'tonal':
        return 'bg-gray-600_a15 text-black border-transparent hover:bg-gray-600_a25 active:bg-gray-600_a35';
      default:
        return 'bg-black text-white border-black hover:shadow-button hover:shadow-blue-400 active:shadow-button active:shadow-brand-400 transition-shadow';
    }
  });

  const contextSizeClassName = computed((): string => {
    switch (props.size) {
      case 'medium':
        return 'py-2 px-5';
      case 'small':
        return 'py-1 px-5';
      default:
        return 'py-3 px-6';
    }
  });

  export type { TButtonHierarchy, TButtonSize, TButtonType };
</script>

<style lang="scss" scoped>
  .button:deep(span) {
    @apply align-top leading-6;
  }
</style>
